import {
    computed,
    defineComponent,
    onMounted,
    Ref,
    ref,
} from "vue";

import {
    type RouteLocationNormalizedLoaded,
    useRoute,
} from "vue-router";
import { useLogin }     from "../utils.js";
import { useLocale }    from "../locales/i18n.js";
import Profile          from "./profile.js";
import Intro            from "./public/intro.js";
import Error401         from "./public/401.js";
import Store, { type GuildData } from "../store.js";
import BootOverlay      from "../components/BootOverlay.js";
import AppDock          from "../components/AppDock.js";
import { ContextMenuProvider, useContextMenuProvider } from "../components/contextMenuProvider.js";

export type MissingPermissionsResult = {
    viewLogs            : boolean;
    missingChannelIds   : string[];
} | null;

export function resolveMissingPermissions(guild: GuildData | null): MissingPermissionsResult {
    const PermissionFlagsBits = {
        Administrator       : 8n,
        ViewAuditLog        : 128n,
        ViewChannel         : 1024n,
        ReadMessageHistory  : 65536n,
    } as const;

    const RequiredChannelFlags = PermissionFlagsBits.ViewChannel | PermissionFlagsBits.ReadMessageHistory;

    function hasPermissions(permissions: bigint, flags: bigint): boolean {
        return (BigInt(permissions) & flags) === flags;
    }

    const botInstance       = guild?.bot;
    const botPermissions    = botInstance?.permissions;

    if (!botInstance || !botPermissions)
        return null;

    if (hasPermissions(botPermissions.global, PermissionFlagsBits.Administrator))
        return null;

    const missingPermissions = {
        viewLogs            : hasPermissions(botPermissions.global, PermissionFlagsBits.ViewAuditLog),
        missingChannelIds   : [] as string[],
    };

    for (const [ channelId, permissions ] of Object.entries(botPermissions.channels) as [string, bigint][])
        if (!hasPermissions(permissions, RequiredChannelFlags))
            missingPermissions.missingChannelIds.push(channelId);

    if (missingPermissions.viewLogs && missingPermissions.missingChannelIds.length === 0)
        return null;

    return missingPermissions;
}

export function useMissingPermissions(): Ref<MissingPermissionsResult> {
    return computed(() => resolveMissingPermissions(Store.selectedGuild));
}


//////////////////////////////////////////////////
//  APP
//////////////////////////////////////////////////


const LoggedApp = defineComponent({
    setup() {
        const { i18n }                          = useLocale();
        const missingPermissions                = useMissingPermissions();
        const dismissedPermissionWarningGuildId = ref<string | null>(null);
        useContextMenuProvider();

        return () => {
            const selectedGuildId = Store.selectedGuild?.id ?? null;
            const showPermissionWarning = Boolean(
                missingPermissions.value
                && selectedGuildId
                && dismissedPermissionWarningGuildId.value !== selectedGuildId
            );

            return (
                <div id="app" class="app-shell-with-dock">
                    <div class="app-dock-content">
                        {showPermissionWarning && (
                            <aside class="app-permission-notice" role="status" aria-live="polite">
                                <i class="app-permission-notice__icon material-symbols-outlined" aria-hidden="true">warning</i>

                                <div class="app-permission-notice__copy">
                                    <strong>{i18n("errors.bot_missing_required_permissions_title")}</strong>
                                    <span>{i18n("errors.bot_missing_required_permissions")}</span>
                                </div>

                                <router-link to="/settings" class="app-permission-notice__action">
                                    {i18n("errors.bot_missing_required_permissions_action")}
                                </router-link>

                                <button
                                    type="button"
                                    class="app-permission-notice__close"
                                    aria-label={i18n("errors.bot_missing_required_permissions_close")}
                                    title={i18n("errors.bot_missing_required_permissions_close")}
                                    onClick={() => (dismissedPermissionWarningGuildId.value = selectedGuildId)}
                                >
                                    <i class="material-symbols-outlined" aria-hidden="true">close</i>
                                </button>
                            </aside>
                        )}

                        <div class="app-route-content">
                            <router-view />
                        </div>
                    </div>

                    <ContextMenuProvider />
                    <AppDock />
                </div>
            );
        };
    },
});

type SessionBootstrapState = {
    hydrated    : boolean;
    loading     : boolean;
};

function routeRequiresLogin(route: RouteLocationNormalizedLoaded): boolean {
    return route.meta["requireLogin"] === true;
}

function isRootApplicationRoute(route: RouteLocationNormalizedLoaded): boolean {
    return routeRequiresLogin(route) && route.path === "/";
}

export default defineComponent({

    setup() {
        const { i18n }  = useLocale();
        const route     = useRoute();
        const hydrated  = ref(false);

        const [
            loading,
            logged,
        ] = useLogin();

        const shouldRenderBootOverlay = computed(() => {
            if (!routeRequiresLogin(route))
                return false;

            return !hydrated.value || loading.value;
        });

        onMounted(() => {
            hydrated.value = true;
        });

        return () => {
            const renderPage = () => {
                if (!routeRequiresLogin(route))
                    return <router-view />;

                if (!logged.value && isRootApplicationRoute(route))
                    return <Intro />;

                if (loading.value)
                    return null;

                if (!logged.value)
                    return <Error401 />;

                if (route.path === "/profile")
                    return <Profile />;

                if (!Store.me!.guildMember)
                    return <Profile />;

                return <LoggedApp />
            };

            const renderBoot = () => {
                return (
                    <BootOverlay
                        message={i18n("modals.loading.loading_text")}
                        overlay
                        visible={shouldRenderBootOverlay.value}
                    />
                );
            };

            return (
                <>
                    {renderPage()}
                    {renderBoot()}
                </>
            );
        };
    },
});
